import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LangProvider } from "./context/LangContext";
import Header from "./components/Header";
import AdminLayout from "./components/AdminLayout";
import { useMaintenanceMode } from "./hooks/useMaintenanceMode";

// Public Pages
import HomePage from "./pages/HomePage";
import StoriesHub from "./pages/StoriesHub";
import StoryDetailPage from "./pages/StoryDetailPage";
import SubmitStoryPage from "./pages/SubmitStoryPage";
import JourneyPage from "./pages/JourneyPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import HoldingPage from "./pages/HoldingPage";

// Contributor Pages
import ContributorDashboard from "./pages/ContributorDashboard";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import PipelinePage from "./pages/admin/PipelinePage";
import OutputsPage from "./pages/admin/OutputsPage";
import EvidencePage from "./pages/admin/EvidencePage";
import UsersPage from "./pages/admin/UsersPage";
import CountryToolkitPage from "./pages/CountryToolkitPage";

import { Toaster } from "./components/ui/sonner";

// Protected Route for Admin
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#07743F] border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Protected Route for Contributors
const ContributorRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#07743F] border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Maintenance gate — swaps in the HoldingPage for all non-admin visitors while
// maintenance mode is enabled. Login route stays reachable so admins can sign in.
const MaintenanceGate = ({ children }) => {
  const { enabled, loading: statusLoading } = useMaintenanceMode();
  const { isAdmin, loading: authLoading } = useAuth();
  const location = useLocation();

  // Wait until both flags resolve so we do not flash the wrong screen.
  if (statusLoading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#07743F' }}>
        <div className="animate-spin w-8 h-8 border-4 border-[#E4E517] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!enabled) return children;

  // Maintenance ON — allow the login page through so admins can sign in.
  if (location.pathname === '/login') return children;

  // Admins pass straight through to the full app.
  if (isAdmin) return children;

  // Everyone else sees the holding page.
  return <HoldingPage />;
};

// Main App Component
function AppContent() {
  return (
    <MaintenanceGate>
      <div className="App">
        <Routes>
        {/* Public Routes with Header */}
        <Route path="/" element={<><Header /><HomePage /></>} />
        <Route path="/stories" element={<><Header /><StoriesHub /></>} />
        <Route path="/story/:storyId" element={<><Header /><StoryDetailPage /></>} />
        <Route path="/submit" element={<><Header /><SubmitStoryPage /></>} />
        <Route path="/how" element={<><Header /><JourneyPage /></>} />
        
        {/* Auth Routes (no header) */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        
        {/* Contributor Routes */}
        <Route path="/contributor" element={
          <ContributorRoute>
            <Header />
            <ContributorDashboard />
          </ContributorRoute>
        } />
        
        {/* Admin Routes (protected, with AdminLayout) */}
        <Route path="/admin" element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="pipeline" element={<PipelinePage />} />
          <Route path="outputs" element={<OutputsPage />} />
          <Route path="evidence" element={<EvidencePage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="toolkits" element={<CountryToolkitPage />} />
          <Route path="toolkits/:countryId" element={<CountryToolkitPage />} />
        </Route>
        
        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
      </div>
    </MaintenanceGate>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LangProvider>
          <AppContent />
        </LangProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
